// src/pages/billing/TransactionCreatePage.tsx

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, usePayment } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

export const TransactionCreatePage = observer(() => {
  const navigate = useNavigate();
  // CreateTransaction moved from BillingService to PaymentService.ChargePayment.
  const store = usePayment();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    billing_account_id: '',
    description: '',
    amount: '',
    currency: 'CURRENCY_USD',
    invoice_id: '',
    payment_method_id: '',
    idempotency_key: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await store.ChargePayment({
        billing_account: { id: formData.billing_account_id },
        description: formData.description,
        amount: { amount: Math.round(parseFloat(formData.amount || '0') * 100), currency: formData.currency, decimal_places: 2 },
        invoice: formData.invoice_id ? { id: formData.invoice_id } : undefined,
        payment_method_id: formData.payment_method_id || undefined,
        idempotency_key: formData.idempotency_key || undefined,
      } as any);
      navigate('/billing/transactions');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Create Transaction</h1>
        <nav className="text-sm">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/billing/transactions" className="text-muted-foreground hover:text-foreground">Transactions</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.create')}</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Transaction Details</CardTitle>
            <CardDescription>Create a new billing transaction</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input label="Billing Account ID" type="text" value={formData.billing_account_id} onChange={(e) => handleChange('billing_account_id', e.target.value)} placeholder="Billing account ID" required />
            {/* TODO(framework-h14v): RPC CreateTransaction removed from API; this page now issues a charge via PaymentService.ChargePayment, so other transaction types are unavailable. */}
            <Input label="Description" type="text" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Transaction description" required />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Amount" type="text" value={formData.amount} onChange={(e) => handleChange('amount', e.target.value)} placeholder="0.00" required />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Currency</label>
                <Select
                  value={formData.currency}
                  onChange={(e) => handleChange('currency', e.target.value)} className="w-full">
                  <option value="CURRENCY_USD">USD</option>
                  <option value="CURRENCY_EUR">EUR</option>
                  <option value="CURRENCY_RUB">RUB</option>
                  <option value="CURRENCY_GBP">GBP</option>
                </Select>
              </div>
            </div>
            <Input label="Invoice ID (optional)" type="text" value={formData.invoice_id} onChange={(e) => handleChange('invoice_id', e.target.value)} placeholder="Link to invoice" />
            <Input label="Payment Method ID (optional)" type="text" value={formData.payment_method_id} onChange={(e) => handleChange('payment_method_id', e.target.value)} placeholder="Payment method" />
            <Input label="Idempotency Key (optional)" type="text" value={formData.idempotency_key} onChange={(e) => handleChange('idempotency_key', e.target.value)} placeholder="Unique key" />
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to="/billing/transactions"><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.creating') : 'Create Transaction'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default TransactionCreatePage;
